/**
 * Founder loop (gap increment I2) — typed client for ai-orchestrator.
 *
 *   R1  /api/v1/ai/product-brief           URL → product brief (extract / get / put / seed / competitors)
 *       /api/v1/ai/product-brief/briefs    several briefs (one per product, one primary; plan cap
 *                                          `product_briefs_max` → 402, `over_limit` → read-only 409)
 *   R3  /api/v1/ai/moves                   Daily Moves (/today) + founder settings
 *   R4  /api/v1/ai/marketing/strategy/{id} weekly review, check-in, plan diff, earned autonomy
 *
 * Honesty contract: 404/501/503 → {@link isFounderUnavailable} ("bientôt disponible");
 * a 402 is the global AI-credit refill UX (apiFetch emits it); a 403
 * `feature_not_in_plan` is the plan gate. Nothing here publishes or sends anything.
 */

import { api, ApiError } from '@/lib/api/client';

// ---------------------------------------------------------------------------
// R1 — Product brief
// ---------------------------------------------------------------------------

export type FounderStage = 'exploring' | 'building' | 'built_no_users' | 'selling' | 'other';
export const FOUNDER_STAGES: FounderStage[] = ['exploring', 'building', 'built_no_users', 'selling', 'other'];

export type BriefSourceType = 'url' | 'app_store' | 'play_store' | 'text';

export interface FounderGoal {
  label?: string | null;
  metric: 'users' | 'mrr' | 'other';
  target?: number | null;
  currency?: string | null;
}

export interface BriefPrice {
  plan?: string | null;
  price?: number | null;
  price_text?: string | null;
  currency?: string | null;
  currency_assumed?: boolean;
  period?: string | null;
  promo?: string | null;
  source_url?: string | null;
  verbatim?: boolean;
}

export interface BriefAudience {
  from_copy?: string | null;
  from_pricing?: string | null;
  from_tone?: string | null;
  consensus?: string | null;
  agreement?: number;
  derived_by?: string;
  edited?: boolean;
}

export interface BriefSections {
  name?: string | null;
  summary?: string | null;
  hero?: { headline?: string | null; subheadline?: string | null; verbatim?: boolean; source_url?: string | null };
  cta?: { primary?: string | null; secondary?: string[]; verbatim?: boolean };
  offer?: string | null;
  pricing?: BriefPrice[];
  promos?: string[];
  geo?: { country?: string | null; currency?: string | null; locale?: string | null; assumed?: boolean; basis?: string[] };
  audience?: BriefAudience;
  trust?: { present?: { key: string; evidence?: string }[]; missing?: string[] };
  tone?: { descriptors?: string[]; voice?: string; derived_by?: string };
  key_messages?: string[];
  store?: Record<string, unknown>;
  [extra: string]: unknown;
}

export interface BriefPage {
  url: string | null;
  kind: string;
  status: string;
  reason?: string;
  title?: string | null;
  chars?: number;
}

export interface ProductBrief {
  id: string;
  /** Founder label (rename); the product name itself is `sections.name`. */
  name?: string | null;
  display_name?: string;
  /** The default brief for the legacy endpoints, /today and the launch kit. */
  is_primary?: boolean;
  /** Above the plan cap after a downgrade: read-only (reads + delete allowed). */
  over_limit?: boolean;
  /** False for a brief created empty and never analysed yet. */
  extracted?: boolean;
  source_type: BriefSourceType;
  source_url: string | null;
  ownership_attested: boolean;
  attested_at: string | null;
  stage: FounderStage | null;
  goal: FounderGoal | null;
  sections: BriefSections;
  pages: BriefPage[];
  llm_available: boolean;
  model: string | null;
  version: number;
  versions: number;
  seeded: { strategy_id: string | null; offering_id: string | null; voice_profile_id: string | null; seeded_at: string | null };
  created_at: string | null;
  updated_at: string | null;
}

export interface ExtractInput {
  source_type: BriefSourceType;
  url?: string;
  text?: string;
  ownership_attested: boolean;
  stage?: FounderStage;
  goal?: FounderGoal;
}

export interface ExtractResult {
  needs_text: boolean;
  reason?: 'client_rendered' | 'blocked_robots' | 'no_text';
  message?: string;
  pages?: BriefPage[];
  brief: ProductBrief | null;
  llm_available?: boolean;
}

const BRIEF = '/api/v1/ai/product-brief';
const BRIEFS = `${BRIEF}/briefs`;
const bid = (id: string) => `${BRIEFS}/${encodeURIComponent(id)}`;

export interface BriefSummary {
  id: string;
  name: string | null;
  display_name: string;
  product_name: string | null;
  is_primary: boolean;
  over_limit: boolean;
  extracted: boolean;
  source_type: BriefSourceType;
  source_url: string | null;
  stage: FounderStage | null;
  version: number;
  created_at: string | null;
  updated_at: string | null;
}

export interface BriefList {
  briefs: BriefSummary[];
  count: number;
  /** Plan cap (`-1` unlimited, `null` when the plan could not be resolved). */
  limit: number | null;
  used: number;
  feature: string;
  primary_id: string | null;
}

/** Every brief of the workspace (oldest first) + the usage meter. */
export function listBriefs(): Promise<BriefList> {
  return api.get<BriefList>(BRIEFS);
}

/** A new empty brief (402 `plan_limit_exceeded` at the plan cap). */
export async function createBrief(name?: string): Promise<ProductBrief> {
  const res = await api.post<{ brief: ProductBrief }>(BRIEFS, name ? { name } : {});
  return res.brief;
}

export async function getBriefById(id: string): Promise<ProductBrief> {
  const res = await api.get<{ brief: ProductBrief }>(bid(id));
  return res.brief;
}

export function updateBriefById(
  id: string,
  body: { name?: string; sections?: Partial<BriefSections>; stage?: FounderStage; goal?: FounderGoal },
): Promise<{ brief: ProductBrief; changed: string[] }> {
  return api.put(bid(id), body);
}

export function deleteBrief(
  id: string,
): Promise<{ deleted: boolean; id: string; primary_id: string | null; promoted_id: string | null }> {
  return api.delete(bid(id));
}

export async function setPrimaryBrief(id: string): Promise<ProductBrief> {
  const res = await api.post<{ brief: ProductBrief }>(`${bid(id)}/primary`);
  return res.brief;
}

export function extractBriefInto(id: string, input: ExtractInput): Promise<ExtractResult> {
  return api.post<ExtractResult>(`${bid(id)}/extract`, input);
}

export async function listBriefVersionsById(id: string): Promise<BriefVersion[]> {
  const res = await api.get<{ versions: BriefVersion[] }>(`${bid(id)}/versions`);
  return res?.versions ?? [];
}

export function seedFromBriefId(id: string): Promise<SeedResult> {
  return api.post<SeedResult>(`${bid(id)}/seed`, {});
}

export function analyzeCompetitorsFor(id: string, urls: string[]): Promise<CompetitorsResult> {
  return api.post<CompetitorsResult>(`${bid(id)}/competitors`, { urls });
}

export function getCompetitorsFor(id: string): Promise<CompetitorsResult> {
  return api.get<CompetitorsResult>(`${bid(id)}/competitors`);
}

export function extractBrief(input: ExtractInput): Promise<ExtractResult> {
  return api.post<ExtractResult>(`${BRIEF}/extract`, input);
}

export async function getBrief(): Promise<ProductBrief | null> {
  const res = await api.get<{ brief: ProductBrief | null }>(BRIEF);
  return res?.brief ?? null;
}

export function updateBrief(body: {
  sections?: Partial<BriefSections>;
  stage?: FounderStage;
  goal?: FounderGoal;
}): Promise<{ brief: ProductBrief; changed: string[] }> {
  return api.put(BRIEF, body);
}

export interface BriefVersion {
  version: number;
  change: 'extract' | 'edit';
  changed_fields: string[] | null;
  created_at: string | null;
}

export async function listBriefVersions(): Promise<BriefVersion[]> {
  const res = await api.get<{ versions: BriefVersion[] }>(`${BRIEF}/versions`);
  return res?.versions ?? [];
}

export interface SeedResult {
  seeded: {
    strategy: { id: string; name: string; positioning: string; objectives: number; plan_items: number } | null;
    offering: { id?: string; name?: string; status: string; http_status?: number } | null;
    voice: { id?: string; name?: string; samples?: number; status: string; limit?: number; used?: number } | null;
  };
  brief: ProductBrief;
}

export function seedFromBrief(): Promise<SeedResult> {
  return api.post<SeedResult>(`${BRIEF}/seed`, {});
}

export interface CompetitorSnapshot {
  id: string;
  url: string;
  status: 'ok' | 'blocked_robots' | 'client_rendered' | 'error';
  reason: string | null;
  name: string | null;
  positioning: { headline?: string | null; subheadline?: string | null; claims?: string[]; cta?: string | null } | null;
  pricing: BriefPrice[];
  fetched_at: string | null;
}

export interface CompetitorAnalysis {
  positioning_diff?: { only_you?: string[]; shared?: string[]; they_claim?: string[] };
  pricing_comparison?: string;
  gap_you_can_own?: string;
  derived_by?: string;
}

export interface CompetitorsResult {
  competitors: CompetitorSnapshot[];
  analysis: CompetitorAnalysis | null;
  fetched_at?: string | null;
  llm_available?: boolean;
}

export function analyzeCompetitors(urls: string[]): Promise<CompetitorsResult> {
  return api.post<CompetitorsResult>(`${BRIEF}/competitors`, { urls });
}

export function getCompetitors(): Promise<CompetitorsResult> {
  return api.get<CompetitorsResult>(`${BRIEF}/competitors`);
}

// ---------------------------------------------------------------------------
// R3 — Daily moves
// ---------------------------------------------------------------------------

export type MoveStatus = 'open' | 'completed' | 'skipped';

export interface DailyMove {
  id: string;
  date: string;
  rank: number;
  kind: string;
  source_key: string;
  title: string;
  steps: string[];
  deep_link: string | null;
  deep_link_context: { type?: 'composer' | 'page'; prefill?: { text?: string }; strategy_id?: string; item_id?: string };
  evidence: { why?: string; source?: string; ref?: string | null; signal?: string | null };
  est_minutes: number;
  status: MoveStatus;
  skip_reason: string | null;
  completed_at: string | null;
  personalized_by: 'llm' | 'rules' | null;
  now: boolean;
}

export interface SourceStatus {
  status: 'ok' | 'empty' | 'unavailable' | 'not_enabled';
  reason?: string;
  [extra: string]: unknown;
}

export interface MovesDay {
  date: string;
  moves: DailyMove[];
  open_count: number;
  completed_count: number;
  skipped_count: number;
  streak: number;
  sources: Record<string, SourceStatus>;
  generated_at: string | null;
  llm_available?: boolean;
}

const MOVES = '/api/v1/ai/moves';

/** The day's moves, grounded on `briefId` (default: the primary brief). */
export function getMoves(date?: string, briefId?: string): Promise<MovesDay> {
  const qs = new URLSearchParams();
  if (date) qs.set('date', date);
  if (briefId) qs.set('brief_id', briefId);
  const q = qs.toString();
  return api.get<MovesDay>(q ? `${MOVES}?${q}` : MOVES);
}

export function regenerateMoves(lang?: string, briefId?: string): Promise<MovesDay> {
  const body: { lang?: string; brief_id?: string } = {};
  if (lang) body.lang = lang;
  if (briefId) body.brief_id = briefId;
  return api.post<MovesDay>(`${MOVES}/regenerate`, body);
}

export function completeMove(id: string): Promise<{ move: DailyMove }> {
  return api.post(`${MOVES}/${encodeURIComponent(id)}/complete`);
}

export function skipMove(id: string, reason: string): Promise<{ move: DailyMove }> {
  return api.post(`${MOVES}/${encodeURIComponent(id)}/skip`, { reason });
}

export function reopenMove(id: string): Promise<{ move: DailyMove }> {
  return api.post(`${MOVES}/${encodeURIComponent(id)}/reopen`);
}

export interface LoopState {
  enabled: boolean;
  source: 'server_flag' | 'tenant_override';
  server_flag: boolean;
  tenant_override: boolean | null;
}

export interface FounderSettings {
  tenant_id: string | null;
  timezone: string;
  language: string;
  daily_email_enabled: boolean;
  daily_email_recipient: string | null;
  daily_email_consent_at: string | null;
  daily_email_last_sent: string | null;
  weekly_loop_enabled: boolean | null;
  autonomy_threshold: number;
  loop: LoopState;
}

export async function getFounderSettings(): Promise<FounderSettings> {
  return (await api.get<{ settings: FounderSettings }>(`${MOVES}/settings`)).settings;
}

export async function updateFounderSettings(body: Partial<{
  timezone: string;
  language: string;
  daily_email_enabled: boolean;
  weekly_loop_enabled: boolean;
  weekly_loop_follow_server: boolean;
  autonomy_threshold: number;
}>): Promise<FounderSettings> {
  return (await api.put<{ settings: FounderSettings }>(`${MOVES}/settings`, body)).settings;
}

export function sendMovesEmailNow(): Promise<{ sent: boolean; to: string; date: string }> {
  return api.post(`${MOVES}/email/send-now`);
}

// ---------------------------------------------------------------------------
// R4 — Weekly loop
// ---------------------------------------------------------------------------

export interface CheckinQuestion {
  id: string;
  kind: 'goal_gap' | 'play_outcome' | 'founder_time';
  answer_type: 'number' | 'yes_no_count';
  vars: Record<string, string | number>;
  text: string;
  play_ref?: string | null;
  item_id?: string | null;
  channel?: string | null;
  measured?: number;
  objective_id?: string | null;
}

export interface CheckinAnswer {
  question_id: string;
  value: string | number | boolean;
  count?: number | null;
  note?: string | null;
}

export interface Checkin {
  id: string;
  strategy_id: string;
  week_key: string;
  questions: CheckinQuestion[];
  answers: CheckinAnswer[];
  status: 'open' | 'answered';
  generated_by: string | null;
  replanned_week: string | null;
  answered_at: string | null;
  created_at: string | null;
}

export interface PlanChange {
  id: string;
  week_key: string;
  item_id: string;
  change: 'added' | 'removed' | 'kept';
  channel: string | null;
  title: string | null;
  reasoning: string;
  created_at: string | null;
}

export interface PlayItem {
  id: string;
  play_ref?: string;
  week_key: string;
  channel: string;
  action: string;
  title: string;
  title_en?: string;
  objective_id?: string | null;
  evidence: { why?: string; objective?: string | null; kpi?: string | null; pacing_status?: string | null; source?: string };
  community_rules: string[];
  est_hours: number;
  status: 'planned' | 'approved' | 'rejected' | string;
  origin?: string;
  decision?: { edited?: boolean; at?: string };
}

export interface AutonomyLane {
  lane: string;
  rung: 'draft' | 'suggest' | 'auto';
  streak: number;
  threshold: number;
  proposal_rung: 'suggest' | 'auto' | null;
  proposed_at: string | null;
  last_event: string | null;
  notice_ack_at: string | null;
  notice: string | null;
}

export interface PacingRow {
  goal_id?: string;
  goal?: string;
  kpi?: string;
  pacing_status?: string;
  progress_pct?: number;
  expected_pct?: number;
  forecast_value?: number;
}

export interface WeeklyPayload {
  strategy: { id: string; name: string; status: string };
  loop: LoopState;
  review: {
    summary?: { total: number; behind: number; at_risk: number; on_track: number; proposals: number } | null;
    pacing: PacingRow[];
    adjustments: { kind: string; channel: string; title: string; rationale: string }[];
    week_key?: string | null;
    reviewed_at?: string | null;
    by?: string | null;
  };
  checkin: Checkin | null;
  changes: { week_key: string | null; items: PlanChange[] };
  plan: { current_week: string | null; next_week?: string | null; weeks: Record<string, PlayItem[]> };
  autonomy: AutonomyLane[];
  notice: string;
}

const MKT = '/api/v1/ai/marketing';

export function getWeekly(strategyId: string): Promise<WeeklyPayload> {
  return api.get<WeeklyPayload>(`${MKT}/strategy/${encodeURIComponent(strategyId)}/weekly`);
}

export function runWeeklyReview(strategyId: string, lang?: string): Promise<{ checkin: Checkin | null }> {
  return api.post(`${MKT}/strategy/${encodeURIComponent(strategyId)}/review`, lang ? { lang } : {});
}

export function submitCheckin(
  strategyId: string,
  answers: CheckinAnswer[],
  lang?: string,
): Promise<{ checkin: Checkin; week_key: string; items: PlayItem[]; changes: PlanChange[] }> {
  return api.post(`${MKT}/strategy/${encodeURIComponent(strategyId)}/checkin`, { answers, lang });
}

export function decidePlay(
  strategyId: string,
  itemId: string,
  body: { decision: 'approve' | 'reject'; edited?: boolean; title?: string; reason?: string },
): Promise<{ item: PlayItem; lane: AutonomyLane; proposed: boolean; demoted: boolean }> {
  return api.post(
    `${MKT}/strategy/${encodeURIComponent(strategyId)}/items/${encodeURIComponent(itemId)}/decision`,
    body,
  );
}

export function acceptPromotion(
  strategyId: string,
  lane: string,
  noticeAck: boolean,
): Promise<{ lane: AutonomyLane; launch_modes: Record<string, string> }> {
  return api.post(
    `${MKT}/strategy/${encodeURIComponent(strategyId)}/autonomy/${encodeURIComponent(lane)}/accept`,
    { notice_ack: noticeAck },
  );
}

export function declinePromotion(strategyId: string, lane: string): Promise<{ lane: AutonomyLane }> {
  return api.post(`${MKT}/strategy/${encodeURIComponent(strategyId)}/autonomy/${encodeURIComponent(lane)}/decline`);
}

/** The strategy's saved plan_state (GET /strategy/{id}) — week-bucketed plays. */
export async function getSavedPlan(strategyId: string): Promise<{
  current_week: string | null;
  next_week: string | null;
  weeks: Record<string, PlayItem[]>;
} | null> {
  const res = await api.get<{ strategy?: { plan_state?: Record<string, unknown> | null } }>(
    `${MKT}/strategy/${encodeURIComponent(strategyId)}`,
  );
  const ps = res?.strategy?.plan_state as
    | { current_week?: string; next_week?: string; weeks?: Record<string, PlayItem[]> }
    | null
    | undefined;
  if (!ps || !ps.weeks) return null;
  return { current_week: ps.current_week ?? null, next_week: ps.next_week ?? null, weeks: ps.weeks };
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

export function isFounderUnavailable(err: unknown): boolean {
  return err instanceof ApiError && [404, 501, 503].includes(err.status) && !detailError(err);
}

/** The backend's structured `detail.error` / `error` code, when present. */
export function detailError(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  const body = err.body as { detail?: unknown; error?: unknown } | null;
  if (body && typeof body.error === 'string') return body.error;
  const d = body?.detail;
  if (d && typeof d === 'object' && typeof (d as { error?: unknown }).error === 'string') {
    return (d as { error: string }).error;
  }
  return null;
}

/** ISO week key ("2026-W44") of a date — same format as the backend. */
export function isoWeekKey(d: Date = new Date()): string {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((t.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `${t.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
}
